import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { SESSION_CONSTANTS, MESSAGE_CONSTANTS } from '../../constants';
import { BehaviorSubject } from 'rxjs';
import { HeaderService } from '../api/header.service';
import { AuthService } from '../auth/auth.service';


@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly TOKEN_KEY = SESSION_CONSTANTS.TOKEN_KEY;
  private readonly SESSION_EXPIRY = SESSION_CONSTANTS.SESSION_EXPIRY_MS

  // dialog stream
  showWarningDialog$ = new BehaviorSubject<boolean>(false);

  // Internal timer id for warning
  private warnTimeoutId: any;

  constructor(
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly headerApi: HeaderService,
    private readonly auth: AuthService
  ) { }

  setToken(token: string, expiresAtEpochMs?: number): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    const now = Date.now();
    localStorage.setItem(SESSION_CONSTANTS.TOKEN_ISSUED_AT, String(now));
    if (typeof expiresAtEpochMs === 'number' && expiresAtEpochMs > now) {
      localStorage.setItem(SESSION_CONSTANTS.TOKEN_EXPIRY, String(expiresAtEpochMs));
    }
    this.initSessionWatcher();
  }

  initSessionWatcher(): void {
    if (this.warnTimeoutId) clearTimeout(this.warnTimeoutId);

    const expiryRaw = localStorage.getItem(SESSION_CONSTANTS.TOKEN_EXPIRY);
    const token = this.getToken();
    if (!token || !expiryRaw) { this.showWarningDialog$.next(false); return; }

    const expiryAt = Number(expiryRaw);
    if (!Number.isFinite(expiryAt) || expiryAt <= Date.now()) { this.showWarningDialog$.next(true); return; }

    const FIVE_MIN_MS = 5 * 60 * 1000;
    const msLeft = expiryAt - Date.now();

    if (msLeft <= FIVE_MIN_MS) {
      // Already under 5 minutes: show immediately
      this.showWarningDialog$.next(true);
    } else {
      // Schedule when to show
      const delay = msLeft - FIVE_MIN_MS;
      this.warnTimeoutId = setTimeout(() => this.showWarningDialog$.next(true), delay);
    }
  }

  getToken(): string | null { return localStorage.getItem(this.TOKEN_KEY); }

  isSessionValid(): boolean {
    const token = this.getToken();
    const expiryRaw = localStorage.getItem(SESSION_CONSTANTS.TOKEN_EXPIRY);
    if (!token || !expiryRaw) return false;
    const expiryAt = Number(expiryRaw);
    if (!Number.isFinite(expiryAt) || expiryAt <= 0) return false;
    return Date.now() < expiryAt;
  }

  confirmExtend(): void { this.extendSession(); }

  hideWarningDialog(): void { this.showWarningDialog$.next(false); }

  extendSession(): void {
    const token = this.getToken();
    if (!token) { this.logout(false); return; }

    this.headerApi.extendSession(token).subscribe({
      next: (resp) => {
        const newToken = resp?.access_token ?? token;
        const newExpIso = resp?.token_expires;
        if (newToken) localStorage.setItem(SESSION_CONSTANTS.TOKEN_KEY, newToken);
        if (typeof newExpIso === 'string') {
          const ms = Date.parse(newExpIso);
          if (!Number.isNaN(ms) && ms > Date.now()) {
            localStorage.setItem(SESSION_CONSTANTS.TOKEN_EXPIRY, String(ms));
          }
        }
        this.hideWarningDialog();
        this.messageService.add({ severity: 'success', summary: 'Session Extended', detail: 'Your session has been refreshed successfully.', life: 3000 });
        this.initSessionWatcher();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Extension Failed', detail: 'Could not extend the session. You will be logged out on expiry.', life: 4000 });
      },
    });
  }

  logout(showMessage: boolean = true): void {
    const hadSession = !!localStorage.getItem(this.TOKEN_KEY);

    if (this.warnTimeoutId) clearTimeout(this.warnTimeoutId);

    // Clear ALL persisted data to ensure a clean state
    localStorage.clear();
    // Also clear Gigya cookies to force the Gigya screen to load
    try { this.auth.reInitializeCookie(); } catch {}

    if (showMessage && hadSession) {
      this.messageService.add(MESSAGE_CONSTANTS.SESSION_EXPIRED);
    }
    
    if (!this.router.url.startsWith('/signup') && !this.router.url.startsWith('/resetpassword')) {
      this.router.navigate(['/login']);
    }
  }

}
